import { useEffect, useState } from "react";
import { ContentTypeRender, DateView, Icon } from "nystem-components";
import app from "nystem";

const EnergyGraph = ({ samples, field }) => {
  const values = samples.map((sample) =>
    sample[field].map((value, pos) => (field === "energyTotals" ? (value - samples[0][field][pos]) * 1000 : value)),
  );
  const lowest = Math.min(0, ...values.flat());
  const highest = Math.max(1, ...values.flat());
  const magnitude = 10 ** Math.floor(Math.log10((highest - lowest) / 4));
  const step = [1, 2, 2.5, 5, 10].find((value) => value * magnitude >= (highest - lowest) / 4) * magnitude;
  const minimum = Math.floor(lowest / step) * step;
  const maximum = Math.ceil(highest / step) * step;
  const ticks = Array.from({ length: Math.round((maximum - minimum) / step) + 1 }, (_, pos) => minimum + pos * step);
  return (
    <div className="mt-1" aria-label={field === "energyTotals" ? "Energy since expansion" : "Live power graph"}>
      <div className="relative mb-2 flex items-center pl-8 text-xs text-gray-400">
        <span className="absolute left-0">{field === "energyTotals" ? "Wh since expanded" : "W"}</span>
        <div className="flex gap-3">
          {["A", "B", "C", "Total"].map((phase, pos) => (
            <span
              key={phase}
              className={["text-sky-300", "text-amber-300", "text-violet-300", "text-emerald-300"][pos]}
            >
              {phase}
            </span>
          ))}
        </div>
      </div>
      <div className="relative pl-8">
        <div className="pointer-events-none absolute top-0 left-0 h-28 text-xs text-gray-300 tabular-nums">
          {ticks.map((value) => (
            <span
              key={value}
              className="absolute -translate-y-1/2"
              style={{ top: `${(1 - (value - minimum) / (maximum - minimum)) * 100}%` }}
            >
              {Number(value.toPrecision(12))}
            </span>
          ))}
        </div>
        <svg
          className="h-28 w-full overflow-visible"
          viewBox="0 0 480 112"
          preserveAspectRatio="none"
          aria-label={field === "energyTotals" ? "Recorded energy since expansion" : "Recorded power since expansion"}
        >
          {[0, 1, 2, 3].map((pos) => (
            <line
              key={`time-${pos}`}
              x1={(pos / 3) * 480}
              x2={(pos / 3) * 480}
              y1="0"
              y2="112"
              stroke="currentColor"
              className="text-gray-500/40"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {ticks.map((value) => (
            <line
              key={value}
              x1="0"
              x2="480"
              y1={(1 - (value - minimum) / (maximum - minimum)) * 112}
              y2={(1 - (value - minimum) / (maximum - minimum)) * 112}
              stroke="currentColor"
              className="text-gray-500/40"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {[0, 1, 2, 3].map((pos) => (
            <polyline
              key={pos}
              points={samples
                .map(
                  ({ updatedAt }, index) =>
                    `${((updatedAt - samples[0].updatedAt) / Math.max(1000, samples[samples.length - 1].updatedAt - samples[0].updatedAt)) * 480},${(1 - (values[index][pos] - minimum) / (maximum - minimum)) * 112}`,
                )
                .join(" ")}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
              className={["text-sky-300", "text-amber-300", "text-violet-300", "text-emerald-300"][pos]}
            />
          ))}
        </svg>
        <div className="relative h-4 text-xs leading-tight text-gray-400 tabular-nums">
          {[0, 1, 2, 3].map((pos) => (
            <span
              key={pos}
              className="absolute whitespace-nowrap"
              style={{
                left: `${(pos / 3) * 100}%`,
                transform: `translateX(${pos === 0 ? 0 : pos === 3 ? -100 : -50}%)`,
              }}
            >
              <DateView
                model={{ dateFormat: "timeLong", renderAs: "span" }}
                value={
                  samples[0].updatedAt + ((samples[samples.length - 1].updatedAt - samples[0].updatedAt) * pos) / 3
                }
              />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const ViewButtonEnergyNow = ({ model, view }) => {
  const [energy, setEnergy] = useState([]);
  const [energyTotals, setEnergyTotals] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [samples, setSamples] = useState([]);

  useEffect(() => {
    let mounted = true;
    const update = async () => {
      const { energy = [], energyTotals = [], updatedAt } = await app.connection.emit({ type: model.event });
      if (!mounted) return;
      setEnergy(energy);
      setEnergyTotals(energyTotals);
      view.setValue({ value: Object.fromEntries(model.fields.slice(0, 3).map((field, pos) => [field, energy[pos]])) });
      if (
        expanded &&
        energy.length === 3 &&
        energyTotals.length === 3 &&
        [...energy, ...energyTotals].every(Number.isFinite)
      )
        setSamples((samples) =>
          samples[samples.length - 1]?.updatedAt === updatedAt
            ? samples
            : [
                ...samples,
                {
                  updatedAt,
                  energy: [...energy, energy.reduce((sum, power) => sum + power, 0)],
                  energyTotals: [...energyTotals, energyTotals.reduce((sum, total) => sum + total, 0)],
                },
              ],
        );
    };
    const timer = setInterval(update, 1000);
    update();
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, [model.event, model.fields, view, expanded]);

  return (
    <section aria-label="Shelly electricity consumption">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex shrink-0 items-center gap-2 pt-1">
          <ContentTypeRender items={model.item} />
          <button
            type="button"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded focus:outline-2 focus:outline-sky-300"
            aria-label={expanded ? "Collapse live graphs" : "Expand live graphs"}
            aria-expanded={expanded}
            onClick={() => {
              setExpanded(!expanded);
              setSamples([]);
            }}
          >
            <Icon icon={expanded ? "chevron-up" : "chevron-down"} className="h-3 w-3 text-gray-400" />
          </button>
        </div>
        <div className="ml-auto grid shrink-0 grid-cols-[repeat(4,4.5rem)] gap-3 py-1 text-right tabular-nums">
          {["A", "B", "C", "Total"].map((phase, pos) => (
            <div key={phase}>
              <p className="text-xs text-gray-400">{pos === 3 ? phase : `Phase ${phase}`}</p>
              <p className="whitespace-nowrap text-xl font-semibold leading-tight text-sky-300">
                {energy.length === 3 && energy.every(Number.isFinite)
                  ? Math.round(pos === 3 ? energy.reduce((sum, power) => sum + power, 0) : energy[pos])
                  : "—"}
              </p>
              {expanded && (
                <p className="mt-1 whitespace-nowrap text-sm text-emerald-300">
                  {samples.length && energyTotals.length === 3 && energyTotals.every(Number.isFinite)
                    ? (
                        (pos === 3
                          ? energyTotals.reduce((sum, total) => sum + total, 0) - samples[0].energyTotals[3]
                          : energyTotals[pos] - samples[0].energyTotals[pos]) * 1000
                      ).toFixed(1)
                    : "—"}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
      {expanded &&
        (samples.length ? (
          <EnergyGraph samples={samples} field="energy" />
        ) : (
          <p className="py-3 text-xs text-gray-400">Waiting for live readings…</p>
        ))}
    </section>
  );
};

export default ViewButtonEnergyNow;
